import moment from "moment";
import { ElMessageBox } from "element-plus";
import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { Pagination } from "~/types/pagination";
import { CallHistory, Customer } from "~/types";
import { Ticket } from "~/projects/help-desk/types";
import { Lead, Opportunity } from "~/projects/crm/types";
import { Profile } from "~/types/profile";
import { Task } from "~/projects/task-manager/types/task";
import { TaskByStatus } from "~/projects/task-manager/types/taskByStatus";
import { TaskComment } from "~/projects/task-manager/types/taskComment";
import { TaskAttachment } from "~/projects/task-manager/types/taskAttachment";
import { TaskStatus } from "~/projects/task-manager/types/enums/TaskStatus";
import { TaskableType } from "~/projects/task-manager/types/enums/TaskableType";

// Recursively serialize a (possibly nested) object into FormData using
// Laravel-style bracket notation (e.g. assigned_users[0][id]). Null/undefined
// values are skipped; booleans become "1"/"0".
function objectToFormData(
  data: any,
  formData: FormData = new FormData(),
  parentKey = "",
): FormData {
  if (data === null || data === undefined) return formData;

  if (Array.isArray(data)) {
    data.forEach((value, index) => {
      objectToFormData(value, formData, `${parentKey}[${index}]`);
    });
  } else if (
    typeof data === "object" &&
    !(data instanceof File) &&
    !(data instanceof Blob) &&
    !(data instanceof Date)
  ) {
    Object.entries(data).forEach(([key, value]) => {
      objectToFormData(
        value,
        formData,
        parentKey ? `${parentKey}[${key}]` : key,
      );
    });
  } else {
    let value: string | Blob = data;
    if (data instanceof Date) value = data.toISOString();
    else if (typeof data === "boolean") value = data ? "1" : "0";
    else if (!(data instanceof Blob)) value = String(data);
    formData.append(parentKey, value);
  }

  return formData;
}

export const useTasksStore = definePiniaStore("useTasksStore", {
  state: () => ({
    _tableData: [] as Task[],
    _loading: true,
    _statusTasksLoading: true,
    _pagination: {} as Pagination,
    _filterData: {},
    _statuses: [],
    _statusTasks: [] as TaskByStatus[],
    _priorities: [],
    multipleSelection: [],
    _selectedTaskForEdit: {} as Task,
    _selectedTaskForEditComments: [] as TaskComment[],
    _selectedTaskForEditAttachments: [] as TaskAttachment[],
    _taskComments: {} as Task,
    showEditModal: false,
    showEditDialog: false,
    showCreateDialog: false,
    showCreateModal: false,
    createTaskStatus: "open" as TaskStatus,
    taskableData: null as null | Pick<Task, "taskable_id" | "taskable_type">,
    _taskProfileId: undefined as number | undefined,

    // report
    _agentWiseTaskStatusReports: [],
    _deptWiseTaskStatusReports: [],
    _agentWiseTaskStatusReportsFilter: {},
    _deptWiseTaskStatusReportsFilter: {},
  }),
  actions: {
    async getTasks(pageNo = 1, sortBy = "id", ascending = false) {
      this._loading = true;
      const sortDir = ascending ? "asc" : "desc";
      let url = `task-manager/tasks?page=${pageNo}&sort_column=${sortBy}&dir=${sortDir}`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest<
        TableDataWithPagination<Task>
      >(url);

      if (!error) {
        const { data, ...pagination } = response.data;
        this._tableData = data;
        this._pagination = pagination;
        this._loading = false;
      }
    },
    async exportTable() {
      let url = `task-manager/tasks/export?`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `${key}=${value}&`;
      }

      const date = moment().format("YYYY-MM-DD");
      const fileName = `${"tasks-report"}-${date}`;

      await downloadWithSavePicker(url, fileName);
    },
    async getStatusTask() {
      this._statusTasksLoading = true;
      let url = `task-manager/status-tasks?`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest<TaskByStatus[]>(url);

      if (!error) {
        const { data } = response;
        this._statusTasks = data;
        this._statusTasksLoading = false;
      }
    },
    // Appends the next slice of one board column.
    //
    // The column and the paging cursor are arguments rather than keys parked on
    // _filterData, because `status` there is the agent's own status filter
    // (Filters.vue). Writing the scrolled column's name over it re-scoped every
    // later request — including getTasks(), which serializes the same object —
    // to that one column.
    async getStatusTasksByOffset(status: string, offset: number, length = 10) {
      let url = `task-manager/by-offset?status=${encodeURIComponent(
        status,
      )}&offset=${offset}&length=${length}`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        // The column being paged is narrower than any status the agent could
        // have filtered to, so it wins; the rest of the filters still apply.
        if (key === "status" || key === "offset" || key === "length") continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest<TaskByStatus[]>(url);

      if (!error) {
        const { data } = response;

        if (!data || data.length === 0) {
          return;
        }

        // Only a column already on the board can be scrolled, so a miss means
        // the board was replaced mid-request — appending would build a group
        // with no count behind it.
        const statusGroup = this.$state._statusTasks.find(
          (group) => group.name === status,
        );
        if (!statusGroup) return;

        // Only append if the column is still exactly where this request started.
        // Nothing cancels an in-flight page, so a response can land after the
        // board has been refetched and rebuilt — appending it then would splice
        // a later slice onto a fresh first page and leave a hole in the list.
        if (statusGroup.tasks.length !== offset) return;

        statusGroup.tasks.push(...data);
      }
    },
    async getTask(id: number) {
      const url = `task-manager/tasks/${id}`;
      const { response, error } = await getRequest<Task>(url);

      if (!error) {
        this.$state._task = response.data;
        this.$state._selectedTaskForEdit = response.data;
      }
    },
    async getSelectedTaskForEditComments(id: number) {
      const url = `task-manager/tasks/${id}/comments`;
      const { response, error } = await getRequest<TaskComment[]>(url);

      if (!error) {
        const { data } = response;
        this._selectedTaskForEditComments = data;
      }
    },
    async createTask(
      taskData: Partial<Task>,
      attachments: { file: File; name: string; description: string }[] = [],
    ) {
      if (this.taskableData !== null) {
        taskData.taskable_type = this.taskableData.taskable_type;
        taskData.taskable_id = this.taskableData.taskable_id;
      }

      let payload: Partial<Task> | FormData = taskData;
      let config = {};
      if (attachments.length) {
        const formData = objectToFormData(taskData);
        attachments.forEach((attachment, index) => {
          formData.append(`attachments[${index}][file]`, attachment.file);
          formData.append(`attachments[${index}][name]`, attachment.name);
          formData.append(
            `attachments[${index}][description]`,
            attachment.description ?? "",
          );
        });
        payload = formData;
        config = { headers: { "Content-Type": "multipart/form-data" } };
      }

      const { response, error } = await postRequest<Task>(
        "task-manager/tasks",
        payload,
        true,
        config,
      );
      if (!error) {
        this._filterData = {};
        return response.data;
      }
      this.taskableData = null;
      return error.response?.data;
    },
    async createFollowUpTask(taskData: Partial<Task>): Promise<Task> {
      if (this.taskableData !== null) {
        taskData.taskable_type = this.taskableData.taskable_type;
        taskData.taskable_id = this.taskableData.taskable_id;
      }

      const { response, error } = await postRequest<Task>(
        "task-manager/follow-up-tasks",
        taskData,
      );

      this.taskableData = null;

      if (!error) {
        return response.data;
      } else {
        ElMessageBox({
          title: "Follow up task creation failed",
          message:
            error?.response?.data?.message ||
            "An error occurred while creating the follow-up task.",
          type: "error",
        });

        // Throwing an error here ensures the return type is satisfied
        throw new Error(
          error?.response?.data?.message || "Follow up task creation failed",
        );
      }
    },

    async deleteTask(id: Number) {
      await deleteRequest(`task-manager/tasks/${id}`);
    },
    async deleteComment(id: Number) {
      await deleteRequest(`task-manager/comments/${id}`);
    },
    async createComments(commentData: TaskComment) {
      const { response, error } = await postRequest<TaskComment>(
        "task-manager/comments",
        commentData,
      );
      if (!error) {
        return response.data;
      }
      return error;
    },
    async updateTask(taskData: Partial<Task>) {
      const { response, error } = await putRequest<Task>(
        `task-manager/tasks/${taskData.id}`,
        taskData,
      );
      if (!error) {
        return response.data;
      }
      return error;
    },
    async moveTask(taskData: Partial<Task>) {
      const { response, error } = await postRequest<Task>(
        `task-manager/tasks/${taskData.id}/move`,
        taskData,
      );
      if (!error) {
        return response.data;
      }
      if (error) {
        let errorMessage = "";

        if (error.response) {
          const data = error.response.data;

          if (Array.isArray(data.errors)) {
            errorMessage = data.errors.join("<br/>");
          } else if (data.errors && typeof data.errors === "object") {
            const formattedErrors = [];
            for (const [key, value] of Object.entries(data.errors)) {
              const messages = Array.isArray(value) ? value : [value];
              formattedErrors.push(...messages);
            }
            errorMessage = formattedErrors.join("<br/>");
          } else if (data.message) {
            errorMessage = data.message;
          } else {
            errorMessage = "Unknown error occurred.";
          }
        } else {
          errorMessage = error.message || "Unknown error occurred.";
        }

        ElMessageBox({
          title: "Operation failed",
          message: errorMessage,
          type: "error",
          dangerouslyUseHTMLString: true,
        });

        return;
      }
      return error.response?.data;
    },
    async getStatuses() {
      const { response, error } = await getRequest(
        "task-manager/tasks/status-list",
      );
      if (!error) {
        this._statuses = response.data as [];
      }
    },
    async getPriorities() {
      const { response, error } = await getRequest(
        "task-manager/tasks/priority-list",
      );
      if (!error) {
        this._priorities = response.data as [];
      }
    },
    async getTaskable() {
      return await getRequest<
        CallHistory | Customer | Ticket | Lead | Opportunity
      >(`task-manager/tasks/${this._selectedTaskForEdit.id}/taskable`);
    },
    async getTaskComments(id: number) {
      const { response, error } = await getRequest<Task>(
        `task-manager/tasks/${id}/comments`,
      );
      if (!error) {
        this._taskComments = response.data;
      }
    },
    async getAgentWiseTaskStatusReports() {
      let url = `task-manager/reports/agent-wise-task-status-report?`;
      for (const [key, value] of Object.entries(
        this._agentWiseTaskStatusReportsFilter,
      )) {
        if (!value || (Array.isArray(value) && value.length === 0)) continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest(url);
      if (!error) {
        this._agentWiseTaskStatusReports = response.data;
      }
    },
    async getDeptWiseTaskStatusReports() {
      let url = `task-manager/reports/department-wise-task-status-report?`;
      for (const [key, value] of Object.entries(
        this._deptWiseTaskStatusReportsFilter,
      )) {
        if (!value || (Array.isArray(value) && value.length === 0)) continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest(url);
      if (!error) {
        this._deptWiseTaskStatusReports = response.data;
      }
    },
    async exportAgentWiseTaskStatusReports() {
      let url = `task-manager/reports/agent-wise-task-status-report/export?`;
      for (const [key, value] of Object.entries(
        this._agentWiseTaskStatusReportsFilter,
      )) {
        if (value === null) continue;
        url += `${key}=${value}&`;
      }

      const date = moment().format("YYYY-MM-DD");
      const fileName = `${"agent-wise-task-status-report"}-${date}`;

      await downloadWithSavePicker(url, fileName);
    },
    async exportDeptWiseTaskStatusReports() {
      let url = `task-manager/reports/department-wise-task-status-report/export?`;
      for (const [key, value] of Object.entries(
        this._deptWiseTaskStatusReportsFilter,
      )) {
        if (value === null) continue;
        url += `${key}=${value}&`;
      }

      const date = moment().format("YYYY-MM-DD");
      const fileName = `${"department-wise-task-status-report"}-${date}`;

      await downloadWithSavePicker(url, fileName);
    },
    setAgentWiseTaskStatusReportsFilter(data: object) {
      this._agentWiseTaskStatusReportsFilter = data;
    },
    setDeptWiseTaskStatusReportsFilter(data: object) {
      this._deptWiseTaskStatusReportsFilter = data;
    },
    setTaskProfileId(id: number) {
      this._taskProfileId = id;
    },
    setFilterData(filterData: object) {
      this._filterData = filterData;
    },
    setSelectedTaskForEdit(task: Task) {
      this._selectedTaskForEdit = task;
    },
    setEditModalVisibility(val: boolean) {
      this.showEditModal = val;
    },
    setCreateModalVisibility(val: boolean) {
      this.showCreateModal = val;
    },
    setEditDialogVisibility(val: boolean) {
      this.showEditDialog = val;
    },
    setCreateDialogVisibility(val: boolean) {
      this.showCreateDialog = val;
    },
    selectionChange(arr: any) {
      this.multipleSelection = arr;
    },
    setTaskableData(taskableData: Pick<Task, "taskable_id" | "taskable_type">) {
      this.$state.taskableData = taskableData;
    },
    relocateTask(taskId: number, newStatus: TaskStatus, newPosition: number) {
      let taskToMove = {} as Task;
      let previousGroup:
        | { name: string; tasks: Task[]; count: number }
        | undefined;

      this._statusTasks.find((statusGroup) => {
        const taskIndex = statusGroup.tasks.findIndex(
          (task) => task.id === taskId,
        );

        if (taskIndex !== -1) {
          taskToMove = statusGroup.tasks.splice(taskIndex, 1)[0];
          previousGroup = statusGroup;
        }
      });

      if (!taskToMove) {
        console.log(`Task with ID ${taskId} not found.`);
        return;
      }

      if (previousGroup) {
        previousGroup.count = Math.max(0, (previousGroup.count || 0) - 1);
      }

      taskToMove.status = newStatus;
      taskToMove.position = newPosition;

      let targetGroup = this._statusTasks.find(
        (group) => group.name === newStatus,
      );

      if (!targetGroup) {
        targetGroup = { name: newStatus, tasks: [], count: 0 };
        this._statusTasks.push(targetGroup);
      }

      targetGroup.count = (targetGroup.count || 0) + 1;

      const insertIndex = targetGroup.tasks.findIndex(
        (task) => task.position > newPosition,
      );

      if (insertIndex === -1) {
        targetGroup.tasks.push(taskToMove);
      } else {
        targetGroup.tasks.splice(insertIndex, 0, taskToMove);
      }
    },
  },
  getters: {
    tableData: (state) => state._tableData,
    statusTasks: (state) => state._statusTasks,
    pagination: (state) => state._pagination,
    statuses: (state) => state._statuses,
    priorities: (state) => state._priorities,
    selectedTaskForEdit: (state) => state._selectedTaskForEdit,
    showEditOptions(state) {
      return state.multipleSelection.length > 0;
    },
    taskComments: (state) => state._taskComments,
    taskProfileId: (state) => state._taskProfileId,
    agentWiseTaskStatusReports: (state) => state._agentWiseTaskStatusReports,
    deptWiseTaskStatusReports: (state) => state._deptWiseTaskStatusReports,
  },
});
